import * as React from "react";
import {
	Button,
	Checkbox,
	FormGroup,
	FormTreeSelect,
	Input,
	Modal,
	ModalContent,
	RotateLoader,
	Select,
	Switch,
	TabSelect,
	Textarea,
} from "@lib/components";
import { getMenuTreeNodes } from "../../../../components/v2/menuprintereligibleitems";

export const KDS_ICONS: Array<{ value: T.Schema.Restaurant.KdsStationIcon; label: string }> = [
	{ value: "document", label: "Document" },
	{ value: "fry", label: "Fry" },
	{ value: "grill", label: "Grill" },
	{ value: "pasta", label: "Pasta" },
	{ value: "dessert", label: "Dessert" },
];

export const KDS_SERVICES: Array<{ value: T.Schema.Restaurant.KdsServiceType; label: string }> = [
	{ value: "dine_in", label: "Dine In" },
	{ value: "pickup", label: "Take Out" },
	{ value: "delivery", label: "Deliveries" },
];

export const KDS_ORIGINS: Array<{ value: T.Schema.Restaurant.KdsOrderOrigin; label: string }> = [
	{ value: "ubereats", label: "Uber Eats" },
	{ value: "doordash", label: "DoorDash" },
	{ value: "grubhub", label: "GrubHub" },
];

export function blankStation(): T.Schema.Restaurant.KdsStation {
	return {
		id: `kds_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
		name: "",
		icon: "document",
		description: "",
		active: true,
		eligible_items: [],
		service_types: KDS_SERVICES.map(service => service.value),
		sources: {
			pos: true,
			online: true,
			origins: KDS_ORIGINS.map(origin => origin.value),
		},
	};
}

const TAB_VALUES = [
	{ label: "General", value: "0" },
	{ label: "Customisation", value: "1" },
];

interface Props {
	station: T.Schema.Restaurant.KdsStation;
	isNew: boolean;
	store: {
		restaurant: T.Schema.Restaurant.RestaurantSchema | null;
		isNewMenuSchema?: boolean;
		view: { screen_width: number };
	};
	saving: boolean;
	onClose: () => void;
	onSave: (station: T.Schema.Restaurant.KdsStation) => void;
}

interface State {
	tab: string;
	draft: T.Schema.Restaurant.KdsStation;
}

export class KitchenStationEditor extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { tab: "0", draft: { ...props.station, sources: { ...props.station.sources } } };
	}

	setDraft = (patch: Partial<T.Schema.Restaurant.KdsStation>) => {
		this.setState(state => ({ draft: { ...state.draft, ...patch } }));
	};

	toggleService = (value: T.Schema.Restaurant.KdsServiceType) => {
		const current = this.state.draft.service_types || [];
		const next = current.includes(value) ? current.filter(item => item !== value) : [...current, value];
		this.setDraft({ service_types: next });
	};

	toggleOrigin = (value: T.Schema.Restaurant.KdsOrderOrigin) => {
		const sources = this.state.draft.sources || {};
		const current = sources.origins || [];
		const origins = current.includes(value) ? current.filter(item => item !== value) : [...current, value];
		this.setDraft({ sources: { ...sources, origins } });
	};

	submit = (e: React.FormEvent) => {
		e.preventDefault();
		this.props.onSave(this.state.draft);
	};

	render() {
		const { saving, onClose, store, isNew } = this.props;
		const { tab, draft } = this.state;
		const sources = draft.sources || {};
		const nodes = getMenuTreeNodes(store);
		return (
			<Modal id="kds-station-editor" width="md" alignTop={true} active={true} close={onClose}>
				<ModalContent>
					<h3>{isNew ? "Create Kitchen Station" : "Edit Kitchen Station"}</h3>
				</ModalContent>

				<TabSelect
					id="kds-station-tab-options"
					className="border-white-tb-15"
					hasBorder={true}
					screenWidth={store.view.screen_width}
					onChange={v => this.setState({ tab: v.value })}
					value={tab}
					values={TAB_VALUES}
				/>

				<form onSubmit={this.submit}>
					{tab === "0" && (
						<ModalContent>
							<FormGroup title="Name" help="Shown on the kitchen display and in this dashboard">
								<Input
									type="text"
									value={draft.name}
									required={true}
									onChange={e => this.setDraft({ name: e.target.value })}
								/>
							</FormGroup>
							<FormGroup title="Icon">
								<Select
									value={draft.icon || "document"}
									options={KDS_ICONS}
									onChange={e => this.setDraft({ icon: e.target.value as T.Schema.Restaurant.KdsStationIcon })}
								/>
							</FormGroup>
							<FormGroup title="Description" help="Optional note to help staff identify this station">
								<Textarea
									value={draft.description || ""}
									onChange={e => this.setDraft({ description: e.target.value })}
								/>
							</FormGroup>
							<FormGroup title="Active" help="When on, orders will be displayed on this station's screen">
								<Switch
									id="kds-station-active"
									checked={draft.active !== false}
									onChange={e => this.setDraft({ active: e.target.checked })}
								/>
							</FormGroup>
						</ModalContent>
					)}

					{tab === "1" && (
						<ModalContent>
							<FormGroup
								title="Eligible Items"
								help="Select which items are displayed on this station. Leave empty to display every item."
							>
								{nodes.length === 0 ? (
									<p>No menus or items yet.</p>
								) : (
									<FormTreeSelect
										id="kds-eligible-items"
										selected={draft.eligible_items || []}
										nodes={nodes}
										onChange={eligible_items => this.setDraft({ eligible_items })}
									/>
								)}
							</FormGroup>
							<FormGroup title="Order Services" help="Only orders for these services are displayed">
								{KDS_SERVICES.map(service => (
									<Checkbox
										key={service.value}
										id={`kds-service-${service.value}`}
										className="m-b-2"
										label={service.label}
										checked={(draft.service_types || []).includes(service.value)}
										onChange={() => this.toggleService(service.value)}
									/>
								))}
							</FormGroup>
							<FormGroup title="Order Sources" help="Only orders from these sources are displayed">
								<Checkbox
									id="kds-source-pos"
									className="m-b-2"
									label="POS"
									checked={sources.pos === true}
									onChange={e => this.setDraft({ sources: { ...sources, pos: e.target.checked } })}
								/>
								<Checkbox
									id="kds-source-online"
									className="m-b-2"
									label="Online"
									checked={sources.online === true}
									onChange={e => this.setDraft({ sources: { ...sources, online: e.target.checked } })}
								/>
								{KDS_ORIGINS.map(origin => (
									<Checkbox
										key={origin.value}
										id={`kds-origin-${origin.value}`}
										className="m-b-2"
										label={origin.label}
										checked={(sources.origins || []).includes(origin.value)}
										onChange={() => this.toggleOrigin(origin.value)}
									/>
								))}
							</FormGroup>
						</ModalContent>
					)}

					<ModalContent>
						<Button full={true} color="primary" type="submit" disabled={saving}>
							{saving && <RotateLoader size={2} color="white" />}
							{!saving && "Save"}
						</Button>
					</ModalContent>
				</form>
			</Modal>
		);
	}
}
